import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { PortalConfiguration } from '@src/app/model/portal-configuration';
import { PortalManualConfiguration } from '@src/app/model/portal-manual-configuration';
import { JSON } from 'ta-json';

@Injectable({
  providedIn: 'root',
})
export class PayloadService {
  constructor(
    private _apolloService: ApolloService,
  ) {
  }

  /**
   * When calling this function make sure the scope is set correctly in the scopeService
   */
  public async getPayloadString(config: PortalConfiguration, scope: string, key?: string): Promise<string> {
    const newPayload = key ? await this.getPayloadWithKey$(config, key) : await this.getPayload$(config);
    // we map maps to lists, since JSON.stringify does not understand maps!
    newPayload.configuration.configurationPlacement.placements.forEach(
      p => p.xAttachments = Object.fromEntries(p.xAttachments)
    );
    newPayload.skuPositions = Object.fromEntries(newPayload.skuPositions);
    (newPayload.configuration as any).scope = scope;
    return JSON.stringify(newPayload);
  }

  /**
   * When calling this function, make sure the scope is set correctly in the scopeService
   */
  public async getPayload$(config: PortalConfiguration): Promise<PortalManualConfiguration> {
    const fatVars = await this._apolloService.getAllFatVariantsByWoodtype$(config.configurationPlacement.woodType);
    const fatVarsByVarId = new Map(fatVars.map(fv => [fv.variant.id, fv]));

    // It's possible we're missing some archived fatVariants, so we need to fetch them and add them to the map
    const configVariantIds = config.configurationPlacement.placements.map(p => p.variantId);
    const missingFatVarIds = configVariantIds.filter(vId => !fatVarsByVarId.has(vId));
    const missingFatVars = await this._apolloService.getFatVariantsByVariantIds$(
      config.configurationPlacement.woodType,
      missingFatVarIds
    );
    missingFatVars.forEach(fv => fatVarsByVarId.set(fv.variant.id, fv));

    const placementsInfo = config.configurationPlacement.getPlacementsInfo(fatVarsByVarId);
    return await firstValueFrom(this._apolloService.getManualPayload$(config.code, placementsInfo));
  }

  /**
   * When calling this function, make sure the scope is set correctly in the scopeService
   */
  public async getPayloadWithKey$(config: PortalConfiguration, key: string): Promise<PortalManualConfiguration> {
    const fatVars = await this._apolloService.getAllFatVariantsByWoodtypeWithKey$(
      config.configurationPlacement.woodType,
      key
    );
    const fatVarsByVarId = new Map(fatVars.map(fv => [fv.variant.id, fv]));

    // It's possible we're missing some archived fatVariants, so we need to fetch them and add them to the map
    const configVariantIds = config.configurationPlacement.placements.map(p => p.variantId);
    const missingFatVarIds = configVariantIds.filter(vId => !fatVarsByVarId.has(vId));
    const missingFatVars = await this._apolloService.getFatVariantsByVariantIdsWithKey$(
      config.configurationPlacement.woodType,
      missingFatVarIds,
      key
    );
    missingFatVars.forEach(fv => fatVarsByVarId.set(fv.variant.id, fv));

    const placementsInfo = config.configurationPlacement.getPlacementsInfo(fatVarsByVarId);
    return await firstValueFrom(this._apolloService.getManualPayloadWithKey$(config.code, placementsInfo, key));
  }
}
